import React from "react";
import { FieldState } from "~/components/inline-fields-form/types";
import { FolderRequestType } from "../types";
import {
  EditGroupedFolder,
  EditProjectTextField,
  EditFolderDrive,
  EditFolderType,
} from "~/ProjectForm/EditProjectComponents";
import { useGetGroupedFoldersQuery } from "~/refdata/groupedFolderSearch.hotchoc.generated";
import { SharedDrive } from "~/gql/types2";
import styled from "@emotion/styled";

type ProjectFolderFieldProps = {
  formState: Record<string, FieldState>;
  customerId: number;
  folderDrives: SharedDrive[];
};

const FormFieldWrapper = styled("div")`
  display: flex;
  flex-direction: column;
  align-items: start;
  row-gap: 4px;

  > dt {
    display: flex !important;
    justify-content: right;
    padding-top: 5px;
    min-height: 25px;
    text-transform: uppercase;
    font-size: 0.7rem;
  }

  > dd {
    margin: 0;
    & .mdc-list {
      min-width: 300px;
    }
    & .inline-text-field {
      padding-left: 0;
      margin-left: -3px;
      position: relative;
      bottom: 2.5px;
    }
  }

  & .form-field {
    margin-inline: 0 !important;
  }

  & .mdc-text-field-helper-line {
    display: none;
  }
}
`;

const FormWrapper = styled("div")`
  height: 180px;
`;

export const AddFolderFormFields: React.FC<ProjectFolderFieldProps> = (
  props
) => {
  const { formState, customerId, folderDrives } = props;

  return (
    <FormWrapper>
      <FormFieldWrapper>
        <EditFolderType name="folderType" />
      </FormFieldWrapper>

      {formState.folderType?.value == FolderRequestType.ExistingGrouped && (
        <FormFieldWrapper>
          <dt>Grouped folder</dt>
          <dd>
            <GroupedFolderField customerId={customerId} formState={formState} />
          </dd>
        </FormFieldWrapper>
      )}

      {formState.folderType?.value == FolderRequestType.Single && (
        <FormFieldWrapper>
          <dt>Project folder drive letter</dt>
          <dd>
            <EditFolderDrive
              name="projectFolderDriveLetter"
              folderDrives={folderDrives}
            />
          </dd>
        </FormFieldWrapper>
      )}

      {formState.folderType?.value == FolderRequestType.NewGrouped && (
        <FormFieldWrapper>
          <dt>New grouped folder name</dt>
          <dd>
            <EditProjectTextField name="newGroupedFolderName" />
          </dd>
          <dt>New grouped folder drive letter</dt>
          <dd>
            <EditFolderDrive
              name="newGroupedFolderDriveLetter"
              folderDrives={folderDrives}
            />
          </dd>
        </FormFieldWrapper>
      )}
    </FormWrapper>
  );
};

type GroupedFolderFieldProps = {
  customerId: number;
  formState: Record<string, FieldState>;
};

const GroupedFolderField: React.FC<GroupedFolderFieldProps> = (props) => {
  const { customerId, formState } = props;

  const Message = styled("dd")`
    margin: 0 !important;
    padding-left: 2px;
    ${!!formState?.groupedFolder.error &&
    "border: 2px solid var(--mdc-theme-secondary); background-color: white"};
  `;

  const { loading, data } = useGetGroupedFoldersQuery({
    variables: {
      input: {
        customerId: customerId,
      },
    },
  });

  if (loading) {
    return <Message>Loading...</Message>;
  }

  if (!data || data.projectFolders.groupedFolders.length === 0) {
    return <Message>No grouped folders found for this customer</Message>;
  }

  return (
    <EditGroupedFolder
      name="groupedFolder"
      groupedFolders={data.projectFolders.groupedFolders}
    />
  );
};
